import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Mail, AlertCircle, Loader2 } from "lucide-react";

/**
 * Frontend landing page for magic-link emails. The email link points HERE
 * (not at the backend) so aggressive email scanners that pre-fetch URLs only
 * see static HTML and cannot consume the single-use token. The actual consume
 * happens when this component POSTs to `/auth/magic-link/consume` — which
 * only fires when the user's real browser executes our JS.
 */
const MagicLinkConsume = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | error
  const [errorMsg, setErrorMsg] = useState("");
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Sign-in link is missing or malformed.");
      return;
    }

    (async () => {
      try {
        const res = await apiClient.post("/auth/magic-link/consume", { token });
        const { session_token, redirect_to } = res.data || {};
        if (!session_token) throw new Error("No session token returned.");

        localStorage.setItem("session_token", session_token);
        localStorage.removeItem("admin_original_token"); // safety: clear any stale impersonation
        // Hard navigation so AuthContext.checkAuth() picks up the new token
        // from localStorage on the next mount.
        window.location.replace(redirect_to || "/dashboard");
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed.";
        setStatus("error");
        setErrorMsg(detail);
      }
    })();
  }, [params]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-4">
        <div className="text-center space-y-4" data-testid="magic-link-loading">
          <Loader2 className="w-12 h-12 text-[#0D9488] animate-spin mx-auto" />
          <p className="text-slate-300 text-sm">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 text-center space-y-5" data-testid="magic-link-error">
        <div className="w-14 h-14 mx-auto rounded-full bg-red-500/15 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-white mb-2">Sign-in link not valid</h1>
          <p className="text-sm text-slate-300">{errorMsg}</p>
        </div>
        <Button
          onClick={() => navigate("/")}
          className="w-full bg-[#0D9488] hover:bg-[#14B8A6] text-white"
          data-testid="magic-link-request-new-btn"
        >
          <Mail className="w-4 h-4 mr-2" />
          Request a new link
        </Button>
      </div>
    </div>
  );
};

export default MagicLinkConsume;
